import { Button, Checkbox, Input, Textarea } from '@postscript/components';
import { useUpdateProposal } from 'components/brand/offers/hooks/useOfferProposals';
import {
  Actions,
  ProposalContext,
} from 'components/brand/offers/OfferProposalDetail';
import { proposalDescription } from 'components/brand/offers/offerScanPresentation';
import { ProposalVerificationSection } from 'components/brand/offers/ProposalVerification';
import {
  EditProposalRequest,
  OfferProposal,
  ProposalEditableField,
  ProposalEditFormValues,
} from 'components/brand/offers/types';
import {
  benefitFromProposal,
  formatProposalBenefit,
  ProposalBenefitType,
} from 'components/brand/offers/proposalBenefit';
import { StructuredDiscountInput } from 'components/brand/offers/StructuredDiscountInput';
import {
  OFFER_FORM_MAX_DESCRIPTION_CHARS,
  OFFER_FORM_MAX_TITLE_CHARS,
} from 'components/brand/offers/utils';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';

const EditForm = styled(Form)`
  display: grid;
  gap: var(--spacing-2);
`;

const EditNote = styled.p`
  margin: 0;
  color: var(--gray-6);
`;

const FormColumns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(16rem, 0.85fr);
  gap: var(--spacing-4);

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const FormFields = styled.div`
  display: grid;
  gap: var(--spacing-2);
`;

const EvidenceAside = styled.aside`
  display: grid;
  align-content: start;
  gap: var(--spacing-2);
  padding: var(--spacing-3);
  background: var(--gray-1);
  border-radius: var(--border-radius-medium);
`;

const ReadOnlyFact = styled.div`
  display: grid;
  gap: var(--spacing-1);
  color: var(--gray-6);
`;

export const proposalEditSchema = Yup.object({
  offerName: Yup.string()
    .trim()
    .required('Enter a name for this offer')
    .max(
      OFFER_FORM_MAX_TITLE_CHARS,
      `Offer name must be ${OFFER_FORM_MAX_TITLE_CHARS} characters or less`,
    ),
  value: Yup.string().trim().required('Enter the value this offer gives'),
  scopeAndEligibility: Yup.string(),
  redemption: Yup.string(),
  timing: Yup.string(),
  description: Yup.string().max(
    OFFER_FORM_MAX_DESCRIPTION_CHARS,
    `Description must be ${OFFER_FORM_MAX_DESCRIPTION_CHARS} characters or less`,
  ),
});

const editValuesFor = (proposal: OfferProposal): ProposalEditFormValues => ({
  offerName: proposal.consolidatedOfferName ?? proposal.offerName,
  value: proposal.value,
  scopeAndEligibility: proposal.scopeAndEligibility ?? '',
  redemption: proposal.redemption ?? '',
  timing: proposal.timing ?? '',
  description: proposalDescription(proposal),
});

/**
 * Only what the merchant actually changed.
 *
 * The endpoint patches literally -- an omitted field is left alone -- so
 * sending the untouched fields back would overwrite them with a stale read
 * of a row another tab may have edited.
 */
const changedFields = (
  initial: ProposalEditFormValues,
  values: ProposalEditFormValues,
): EditProposalRequest => {
  const changes: EditProposalRequest = {};
  Object.values(ProposalEditableField).forEach((field) => {
    const next = values[field].trim();
    if (next !== initial[field].trim()) changes[field] = next;
  });
  if (values.description.trim() !== initial.description.trim()) {
    changes.description = values.description.trim();
  }
  return changes;
};

interface ProposalEditFormProps {
  proposal: OfferProposal;
  onDone: () => void;
  onSaved?: (proposal: OfferProposal) => void;
  submitLabel?: string;
  formId?: string;
  hideActions?: boolean;
  onCanSubmitChange?: (canSubmit: boolean) => void;
}

/** Correcting what the scanner read. Owns the edit mutation and nothing else. */
export const ProposalEditForm = ({
  proposal,
  onDone,
  onSaved,
  submitLabel = 'Save changes',
  formId,
  hideActions = false,
  onCanSubmitChange,
}: ProposalEditFormProps): JSX.Element => {
  const update = useUpdateProposal();
  // Freeze the optimistic-lock version and comparison baseline for this edit
  // session. Background refetches may update `proposal` while the merchant is
  // typing; submitting against that newer version would bypass the stale-edit
  // guard while carrying old form values.
  const [editSnapshot, setEditSnapshot] = useState(proposal);
  const initialValues = editValuesFor(editSnapshot);
  const initialBenefit = benefitFromProposal(editSnapshot);
  const [benefit, setBenefit] = useState(initialBenefit);
  const [semanticChanged, setSemanticChanged] = useState(false);
  const [descriptionReviewed, setDescriptionReviewed] = useState(false);
  const markSemanticChange = () => {
    setSemanticChanged(true);
    setDescriptionReviewed(false);
  };
  const numericAmount = Number(benefit.amount);
  const hasDecimalSyntax = /^(?:\d+(?:\.\d+)?|\.\d+)$/.test(benefit.amount);
  const amountHasTooManyDecimals =
    benefit.type === ProposalBenefitType.AMOUNT &&
    !/^(?:\d+(?:\.\d{1,2})?|\.\d{1,2})$/.test(benefit.amount);
  const invalidStructuredBenefit =
    benefit.type !== ProposalBenefitType.OTHER &&
    (!benefit.amount ||
      !hasDecimalSyntax ||
      !Number.isFinite(numericAmount) ||
      numericAmount <= 0 ||
      amountHasTooManyDecimals ||
      (benefit.type === ProposalBenefitType.PERCENT && numericAmount > 100));
  const canSubmit =
    !update.isLoading &&
    !invalidStructuredBenefit &&
    (!semanticChanged || descriptionReviewed);

  useEffect(() => {
    onCanSubmitChange?.(canSubmit);
  }, [canSubmit, onCanSubmitChange]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={proposalEditSchema}
      onSubmit={(values) => {
        if (
          invalidStructuredBenefit ||
          (semanticChanged && !descriptionReviewed)
        ) {
          return;
        }
        const benefitChanged =
          JSON.stringify(benefit) !== JSON.stringify(initialBenefit);
        const formattedValue =
          benefit.type === ProposalBenefitType.OTHER
            ? values.value
            : benefitChanged
            ? formatProposalBenefit(benefit)
            : initialValues.value;
        const nextValues = { ...values, value: formattedValue };
        const changes = changedFields(initialValues, nextValues);
        const descriptionNeedsReview = [
          'value',
          'scopeAndEligibility',
          'redemption',
          'timing',
        ].some((field) => field in changes);
        if (descriptionNeedsReview) {
          const reviewedDescription = values.description.trim();
          if (reviewedDescription || 'description' in changes) {
            changes.description = reviewedDescription;
          }
          changes.offerName = values.offerName.trim();
        }
        if (Object.keys(changes).length === 0) {
          if (onSaved) onSaved(editSnapshot);
          else onDone();
          return;
        }
        update.mutate(
          {
            id: editSnapshot.id,
            ...changes,
            ...(editSnapshot.updatedAt && {
              expectedUpdatedAt: editSnapshot.updatedAt,
            }),
          },
          {
            onSuccess: (updatedProposal) => {
              // Advance only from our own successful write. Background query
              // refreshes remain isolated, while an approval retry can reuse
              // this returned version without issuing the same PATCH again.
              setEditSnapshot(updatedProposal);
              if (onSaved) onSaved(updatedProposal);
              else onDone();
            },
          },
        );
      }}
    >
      <EditForm id={formId}>
        <EditNote>
          Review what the scanner found before creating or updating an offer.
          The discount code and benefit type are read only.
        </EditNote>

        <FormColumns>
          <FormFields>
            <Field name="offerName">
              {({ field, meta }: FieldProps) => (
                <Input
                  {...field}
                  label="Offer name"
                  error={meta.touched && Boolean(meta.error)}
                  errorMessage={meta.error}
                  data-testid="proposal-offer-name-input"
                />
              )}
            </Field>

            {benefit.type === ProposalBenefitType.OTHER ? (
              <Field name="value">
                {({ field, meta }: FieldProps) => (
                  <Input
                    {...field}
                    label="Offer value"
                    message="Keep distinct rewards and eligibility details intact."
                    error={meta.touched && Boolean(meta.error)}
                    errorMessage={meta.error}
                    data-testid="proposal-value-input"
                    onChange={(event) => {
                      field.onChange(event);
                      markSemanticChange();
                      setBenefit((current) => ({
                        ...current,
                        freeform: event.target.value,
                      }));
                    }}
                  />
                )}
              </Field>
            ) : (
              <StructuredDiscountInput
                id="proposal-benefit"
                amount={benefit.amount}
                qualifier={benefit.qualifier}
                prefix={
                  benefit.type === ProposalBenefitType.AMOUNT &&
                  benefit.currency
                    ? { USD: '$', GBP: '£', EUR: '€' }[benefit.currency]
                    : undefined
                }
                suffix={
                  benefit.type === ProposalBenefitType.PERCENT ? '%' : undefined
                }
                onAmountChange={(amount) => {
                  markSemanticChange();
                  setBenefit((current) => ({ ...current, amount }));
                }}
                onQualifierChange={(qualifier) => {
                  markSemanticChange();
                  setBenefit((current) => ({ ...current, qualifier }));
                }}
                error={
                  invalidStructuredBenefit
                    ? benefit.type === ProposalBenefitType.PERCENT
                      ? 'Enter a percentage between 0 and 100'
                      : 'Enter a positive amount with no more than two decimal places'
                    : undefined
                }
              />
            )}

            <Field name="description">
              {({ field, meta }: FieldProps) => (
                <Textarea
                  {...field}
                  label="Description"
                  rows={6}
                  message="Review this copy whenever you change the offer value."
                  error={meta.touched && Boolean(meta.error)}
                  errorMessage={meta.error}
                  data-testid="proposal-description-input"
                />
              )}
            </Field>

            <Field name="scopeAndEligibility">
              {({ field }: FieldProps) => (
                <Textarea
                  {...field}
                  label="Applies to"
                  rows={2}
                  data-testid="proposal-scope-input"
                  onChange={(event) => {
                    field.onChange(event);
                    markSemanticChange();
                  }}
                />
              )}
            </Field>

            <Field name="redemption">
              {({ field }: FieldProps) => (
                <Input
                  {...field}
                  label="Redemption"
                  data-testid="proposal-redemption-input"
                  onChange={(event) => {
                    field.onChange(event);
                    markSemanticChange();
                  }}
                />
              )}
            </Field>

            <Field name="timing">
              {({ field }: FieldProps) => (
                <Input
                  {...field}
                  label="Timing"
                  data-testid="proposal-timing-input"
                  onChange={(event) => {
                    field.onChange(event);
                    markSemanticChange();
                  }}
                />
              )}
            </Field>

            {editSnapshot.discountCode && (
              <ReadOnlyFact>
                <strong>Discount code</strong>
                <span>{editSnapshot.discountCode} · Read only</span>
              </ReadOnlyFact>
            )}
            <ReadOnlyFact>
              <strong>Benefit type</strong>
              <span>
                {initialBenefit.type === ProposalBenefitType.PERCENT
                  ? 'Percentage off'
                  : initialBenefit.type === ProposalBenefitType.AMOUNT
                  ? 'Amount off'
                  : 'Other'}{' '}
                · Read only
              </span>
            </ReadOnlyFact>
          </FormFields>

          <EvidenceAside>
            <ProposalVerificationSection
              verification={editSnapshot.verification}
            />
            <strong>Found on your storefront</strong>
            <ProposalContext proposal={editSnapshot} />
          </EvidenceAside>
        </FormColumns>

        {semanticChanged && (
          <Checkbox
            id={`proposal-description-reviewed-${editSnapshot.id}`}
            checked={descriptionReviewed}
            label="I reviewed the name and description against these changes"
            onChange={(event) => setDescriptionReviewed(event.target.checked)}
          />
        )}

        {!hideActions && (
          <Actions>
            <Button
              type="submit"
              variant="primary"
              size="small"
              loading={update.isLoading}
              disabled={!canSubmit}
            >
              {submitLabel}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="small"
              disabled={update.isLoading}
              onClick={onDone}
            >
              Cancel
            </Button>
          </Actions>
        )}
      </EditForm>
    </Formik>
  );
};
